import React from "react";

const PolicySection = () => {
  return (
    <section className="page-section page-section-tight">
      <article className="container legal-page">
        <div className="legal-intro">
          <p>
            Please read this Privacy Policy carefully before accessing or using
            any websites, applications, products, or services provided by
            Lovapex Tech, a Wisconsin limited liability company. By accessing or
            using the Services, you acknowledge that you have read and
            understood this Privacy Policy.
          </p>
        </div>
        <section className="legal-section" id="1-introduction">
          <h2>1. Introduction</h2>
          <p>
            Welcome to Lovapex LLC ("Lovapex", "we", "our", or "us"). We are
            committed to protecting your privacy and ensuring transparency in
            how we collect, use, and safeguard your personal data.
          </p>
          <p>
            This Privacy Policy explains what information we collect, how we use
            it, who we share it with, and your rights under applicable privacy
            laws.
          </p>
          <p>
            By using our website, mobile apps, or services, you consent to the
            data practices described in this policy.
          </p>
        </section>
        <section className="legal-section" id="2-scope-of-policy">
          <h2>2. Scope of Policy</h2>
          <p>This Privacy Policy applies to:</p>
          <ul>
            <li>
              All users of our website, platform, and subscription services.
            </li>
            <li>
              Job seekers ("Clients") who subscribe to Lovapex career management
              and recruiter outreach plans.
            </li>
            <li>
              Employers, recruiters, and business partners who interact with
              Lovapex.
            </li>
            <li>
              All visitors submitting forms, inquiries, or communication through
              our platform.
            </li>
          </ul>
          <p>
            This policy does not apply to external websites or services linked
            through Lovapex.
          </p>
        </section>
        <section className="legal-section" id="3-information-we-collect">
          <h2>3. Information We Collect</h2>
          <div className="legal-subsection">
            <h3>3.1 Personal Identification Information</h3>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Date of birth, if provided</li>
              <li>Nationality and visa status, if applicable</li>
              <li>Residential address or region</li>
            </ul>
          </div>
          <div className="legal-subsection">
            <h3>3.2 Professional and Educational Information</h3>
            <ul>
              <li>Resume or CV</li>
              <li>LinkedIn profile URL</li>
              <li>Education details, certifications, and skills</li>
              <li>
                Employment history, job preferences, and salary expectations
              </li>
              <li>
                Documents supporting work authorization or visa status, such as
                OPT or H-1B documentation
              </li>
            </ul>
          </div>
          <div className="legal-subsection">
            <h3>3.3 Billing and Payment Information</h3>
            <ul>
              <li>
                Payment method, such as credit card, debit card, or PayPal
              </li>
              <li>Billing address</li>
              <li>Transaction records and invoices</li>
            </ul>
          </div>
          <div className="legal-subsection">
            <h3>3.4 Technical Data</h3>
            <ul>
              <li>IP address and browser type</li>
              <li>Device information and operating system</li>
              <li>Log data, time zone, and session activity</li>
              <li>Cookies and tracking identifiers</li>
            </ul>
          </div>
          <div className="legal-subsection">
            <h3>3.5 Communication and Usage Data</h3>
            <ul>
              <li>Chat history with Career Managers or Support</li>
              <li>Emails, forms, and inquiries sent to Lovapex</li>
              <li>Application progress, job matches, and status updates</li>
              <li>Feedback, surveys, and testimonials</li>
            </ul>
          </div>
        </section>
        <section className="legal-section" id="4-how-we-collect-data">
          <h2>4. How We Collect Data</h2>
          <p>We collect data in three ways:</p>
          <ul>
            <li>
              Directly from you when you register, subscribe, fill out forms,
              upload resumes, or communicate with us.
            </li>
            <li>
              Automatically through cookies, analytics tools, and interaction
              logs.
            </li>
            <li>
              From third parties such as Stripe, Resend, LinkedIn, employer
              platforms, or recruiter platforms where you have consented to
              share your information.
            </li>
          </ul>
        </section>
        <section className="legal-section" id="5-how-we-use-your-information">
          <h2>5. How We Use Your Information</h2>
          <p>
            We use your personal data to deliver, improve, and personalize our
            services, including:
          </p>
          <ul>
            <li>Creating and managing your account and subscription.</li>
            <li>
              Providing job search, recruiter outreach, and placement
              assistance.
            </li>
            <li>Processing payments and issuing invoices.</li>
            <li>
              Communicating with you by email, SMS, or WhatsApp for updates and
              guidance.
            </li>
            <li>
              Recommending job opportunities based on your skills and
              preferences.
            </li>
            <li>
              Customizing your experience using data analytics and AI models.
            </li>
            <li>
              Supporting compliance with immigration or employer verification
              requirements, including OPT or H-1B documentation.
            </li>
            <li>
              Conducting fraud prevention, record keeping, and legal compliance
              activities.
            </li>
            <li>
              Sending newsletters, offers, or service updates, which you can opt
              out of at any time.
            </li>
          </ul>
        </section>
        <section
          className="legal-section"
          id="6-legal-basis-for-processing-gdpr-"
        >
          <h2>6. Legal Basis for Processing (GDPR)</h2>
          <p>
            For users located in the EU/EEA, we process personal data under
            these lawful bases:
          </p>
          <ul>
            <li>Contractual Necessity: to deliver subscribed services.</li>
            <li>
              Legitimate Interest: to operate, market, and improve our platform.
            </li>
            <li>
              Legal Obligation: to comply with billing, taxation, or regulatory
              requirements.
            </li>
            <li>
              Consent: for marketing communications or special processing such
              as testimonials.
            </li>
          </ul>
          <p>
            You may withdraw consent at any time without affecting prior lawful
            processing.
          </p>
        </section>
        <section className="legal-section" id="7-data-retention">
          <h2>7. Data Retention</h2>
          <p>
            We retain your data only as long as necessary to fulfill the
            purposes described in this policy or to comply with legal,
            accounting, or tax obligations.
          </p>
          <p>
            In general, account data may be retained for up to 3 years after
            last activity, and communication logs may be retained for 12 to 24
            months.
          </p>
          <p>
            You may request deletion sooner by contacting us as described in
            Section 11.
          </p>
        </section>
        <section
          className="legal-section"
          id="8-cookies-and-tracking-technologies"
        >
          <h2>8. Cookies and Tracking Technologies</h2>
          <p>We use cookies, pixels, and tracking scripts to:</p>
          <ul>
            <li>Improve user experience.</li>
            <li>Analyze site traffic and conversion metrics.</li>
            <li>Remember your login and plan preferences.</li>
          </ul>
          <p>
            You can disable cookies in your browser settings, but some features
            may not function properly.
          </p>
        </section>
        <section className="legal-section" id="9-how-we-share-information">
          <h2>9. How We Share Information</h2>
          <p>
            We never sell or rent your personal data. We only share it in
            limited circumstances:
          </p>
          <div className="legal-subsection">
            <h3>9.1 Service Providers</h3>
            <p>
              We may share information with vetted third-party vendors bound by
              confidentiality, including:
            </p>
            <ul>
              <li>Payment processors such as Stripe and PayPal.</li>
              <li>
                Email and communication tools such as Resend, Twilio, and Google
                Workspace.
              </li>
              <li>Hosting and storage providers such as AWS and Azure.</li>
              <li>
                Analytics and marketing platforms such as Google Analytics and
                Meta Ads.
              </li>
            </ul>
          </div>
          <div className="legal-subsection">
            <h3>9.2 Employer and Recruiter Partners</h3>
            <p>
              If you are a job seeker, your profile or resume may be shared only
              with employers or recruiters who are part of our verified hiring
              network, and only after your explicit consent or action, such as
              applying or booking outreach support.
            </p>
          </div>
          <div className="legal-subsection">
            <h3>9.3 Legal and Compliance</h3>
            <p>
              We may disclose data if required by law, subpoena, or regulatory
              authority, or to protect Lovapex rights, property, or safety.
            </p>
          </div>
          <div className="legal-subsection">
            <h3>9.4 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or asset sale, your data
              may be transferred under similar privacy safeguards.
            </p>
          </div>
        </section>
        <section className="legal-section" id="10-data-security">
          <h2>10. Data Security</h2>
          <p>
            We implement administrative, technical, and physical safeguards to
            protect your information, including:
          </p>
          <ul>
            <li>End-to-end encryption (TLS/SSL) for data in transit.</li>
            <li>
              Encrypted cloud storage using providers such as AWS or Azure.
            </li>
            <li>Role-based access control for internal employees.</li>
            <li>Regular penetration testing and security audits.</li>
            <li>Mandatory NDAs for all team members and partners.</li>
          </ul>
          <p>
            However, no online system is completely secure. You acknowledge that
            data transmission carries inherent risks.
          </p>
        </section>
        <section className="legal-section" id="11-your-rights">
          <h2>11. Your Rights</h2>
          <p>
            Depending on your jurisdiction, you may have the following rights:
          </p>
          <div className="legal-subsection">
            <h3>For All Users</h3>
            <ul>
              <li>Access: request a copy of your personal data.</li>
              <li>Correction: update or fix inaccurate details.</li>
              <li>
                Deletion: request deletion, also known as the "Right to be
                Forgotten".
              </li>
              <li>Restriction: limit processing under specific conditions.</li>
              <li>Portability: receive your data in a transferable format.</li>
              <li>Opt-Out: unsubscribe from marketing communications.</li>
            </ul>
          </div>
          <div className="legal-subsection">
            <h3>Additional Rights (GDPR and CCPA)</h3>
            <ul>
              <li>
                Right to Know: understand what data we collect and how it is
                used.
              </li>
              <li>
                Right to Non-Discrimination: receive equal access regardless of
                privacy choices.
              </li>
              <li>
                Right to Withdraw Consent: stop specific processing, such as
                marketing.
              </li>
            </ul>
          </div>
          <p>
            To exercise any rights, email hello@lovapextech.com with your
            request. We verify identity before fulfilling requests and respond
            within 30 days.
          </p>
        </section>
        <section className="legal-section" id="12-international-data-transfers">
          <h2>12. International Data Transfers</h2>
          <p>
            If you are located outside the United States, your data may be
            transferred to and processed in the U.S., where our servers are
            hosted.
          </p>
          <p>
            We use appropriate protection such as Standard Contractual Clauses
            (SCCs) or equivalent legal mechanisms recognized by GDPR where
            required.
          </p>
        </section>
        <section className="legal-section" id="13-children-s-privacy">
          <h2>13. Children's Privacy</h2>
          <p>Lovapex services are intended for users aged 18 and above.</p>
          <p>
            We do not knowingly collect personal data from minors. If you
            believe a child's data has been collected, please contact
            hello@lovapextech.com for immediate deletion.
          </p>
        </section>
        <section
          className="legal-section"
          id="14-marketing-and-communication-preferences"
        >
          <h2>14. Marketing and Communication Preferences</h2>
          <p>
            By providing your email or phone number, you consent to receive
            communications regarding:
          </p>
          <ul>
            <li>Service updates and placement notifications.</li>
            <li>Career insights and newsletters.</li>
            <li>Promotional offers and events.</li>
          </ul>
          <p>We comply with CAN-SPAM and TCPA marketing laws.</p>
        </section>
        <section className="legal-section" id="15-data-breach-protocol">
          <h2>15. Data Breach Protocol</h2>
          <p>In the event of a data breach:</p>
          <ul>
            <li>
              We will notify affected users and regulators within 72 hours when
              required under GDPR.
            </li>
            <li>
              Our notification will include the nature of the breach, potential
              impact, and remedial measures.
            </li>
            <li>
              Affected systems will be secured and re-audited before
              restoration.
            </li>
          </ul>
        </section>
        <section
          className="legal-section"
          id="16-third-party-links-and-integrations"
        >
          <h2>16. Third-Party Links and Integrations</h2>
          <p>
            Our platform may include links to third-party websites or
            integrations, including LinkedIn, Stripe, and similar services.
          </p>
          <p>
            We are not responsible for their privacy practices. We encourage
            users to review external policies before sharing data.
          </p>
        </section>
        <section className="legal-section" id="17-changes-to-this-policy">
          <h2>17. Changes to This Policy</h2>
          <p>Lovapex may update this Privacy Policy periodically.</p>
          <p>
            Any major changes will be communicated by email or website notice.
            The "Last Updated" date reflects the most recent revision.
          </p>
          <p>
            Continued use of our Services constitutes acceptance of updated
            terms.
          </p>
        </section>
        <section className="legal-section" id="18-contact-us">
          <h2>18. Contact Us</h2>
          <p>
            For privacy inquiries, data requests, or complaints, please contact:
          </p>
          <address className="legal-contact">
            <span>Lovapex LLC</span>
            <span>Email: hello@lovapextech.com</span>
          </address>
        </section>
      </article>
    </section>
  );
};

export default PolicySection;
